import type { ReplayData } from "@Shared/types/ReplayData";

type ReplayWorkerResponse = {
  id: number;
  data?: ReplayData;
  error?: string;
  warmed?: boolean;
};

type PendingRequest = {
  resolve: (data: ReplayData) => void;
  reject: (error: unknown) => void;
};

let worker: Worker | null = null;
let nextRequestId = 1;
const pendingRequests = new Map<number, PendingRequest>();

const getReplayLoaderWorker = () => {
  if (worker) {
    return worker;
  }

  worker = new Worker(new URL("./replay-loader.worker.ts", import.meta.url), { type: "module" });

  worker.onmessage = (event: MessageEvent<ReplayWorkerResponse>) => {
    const { id, data, error } = event.data;
    const pendingRequest = pendingRequests.get(id);

    if (!pendingRequest) {
      return;
    }

    pendingRequests.delete(id);

    if (error) {
      pendingRequest.reject(new Error(error));
      return;
    }

    if (!data) {
      pendingRequest.reject(new Error("Replay worker returned no data"));
      return;
    }

    pendingRequest.resolve(data);
  };

  worker.onerror = error => {
    const pending = Array.from(pendingRequests.values());
    pendingRequests.clear();
    worker?.terminate();
    worker = null;

    pending.forEach(request => {
      request.reject(error instanceof ErrorEvent ? new Error(error.message) : error);
    });
  };

  return worker;
};

export const warmupReplayLoaderWorker = () => {
  getReplayLoaderWorker().postMessage({ type: "warmup" });
};

export const loadReplayInWorker = (downloadUrl: string): Promise<ReplayData> => {
  const replayWorker = getReplayLoaderWorker();
  const id = nextRequestId++;

  return new Promise<ReplayData>((resolve, reject) => {
    pendingRequests.set(id, {
      resolve: data => {
        pendingRequests.delete(id);
        resolve(data);
      },
      reject: error => {
        pendingRequests.delete(id);
        reject(error);
      },
    });

    replayWorker.postMessage({ type: "load", id, downloadUrl });
  });
};
